import Send from "@mui/icons-material/Send";
import {
  Box,
  Button,
  Checkbox,
  DialogContentText,
  FormControlLabel,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ReactNode, useCallback, useRef, useState } from "react";

const NOTIFY_IMMEDIATELY_TOOLTIP =
  "Checking this box will send an email notification with this comment right away, " +
  "rather than waiting for each person's next notification digest";

const CONFIRM_DIALOG_INFO =
  "This will immediately send an email notification with this comment to all Encamp " +
  "staff, reviewers, and signers associated with this submission rather than waiting " +
  "for their next notification digest.";

interface UseNotifyImmediatelyCommentOptions {
  /** Persists the comment; `notifyImmediately` controls the immediate email. */
  onSave: (message: string, notifyImmediately: boolean) => Promise<void>;
}

interface UseNotifyImmediatelyCommentResult {
  /** "Notify immediately" checkbox (with tooltip); render next to the submit button. */
  checkboxNode: ReactNode;
  /** Confirmation dialog; render once anywhere in the surface's tree. */
  dialogNode: ReactNode;
  /**
   * Submit handler for the comment input. Unchecked: saves right away.
   * Checked: opens the confirmation dialog and resolves once the user picks —
   * `{ clear: false }` means they backed out via the X and the draft (and
   * checkbox) should be kept.
   */
  handleSubmit: (message: string) => Promise<{ clear: boolean }>;
}

/**
 * Shared "Notify immediately" behavior for submission comment inputs
 * (SubmissionCommentStream and SubmissionCommentsPanel): checkbox state,
 * the confirmation dialog, and the save flow. The checkbox resets to
 * unchecked after every saved comment so the choice never carries over.
 */
export function useNotifyImmediatelyComment({
  onSave,
}: UseNotifyImmediatelyCommentOptions): UseNotifyImmediatelyCommentResult {
  const [notifyChecked, setNotifyChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  // Synchronous double-click guard (`saving` state won't flush until the next render).
  const savingRef = useRef(false);
  // pendingMessage (state) drives the dialog; pendingRef holds the resolver for
  // the promise handleSubmit returned (a resolver can't live in state).
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const pendingRef = useRef<{
    resolve: (result: { clear: boolean }) => void;
  } | null>(null);

  const finishPending = useCallback((result: { clear: boolean }) => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setPendingMessage(null);
    pending?.resolve(result);
  }, []);

  const handleSubmit = useCallback(
    async (message: string): Promise<{ clear: boolean }> => {
      if (!notifyChecked) {
        await onSave(message, false);
        setNotifyChecked(false);
        return { clear: true };
      }
      return new Promise((resolve) => {
        // Ignore a rapid double-submit before the modal paints — it would
        // overwrite pendingRef and orphan the first promise.
        if (pendingRef.current) {
          resolve({ clear: false });
          return;
        }
        pendingRef.current = { resolve };
        setPendingMessage(message);
      });
    },
    [notifyChecked, onSave]
  );

  const saveAndResolve = useCallback(
    async (notifyImmediately: boolean) => {
      if (pendingMessage === null || savingRef.current) return;
      savingRef.current = true;
      setSaving(true);
      try {
        await onSave(pendingMessage, notifyImmediately);
        setNotifyChecked(false);
        finishPending({ clear: true });
      } catch {
        // onSave surfaces its own error (Apollo onError); close the dialog but
        // keep the draft (clear:false) so the user can retry.
        finishPending({ clear: false });
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [onSave, finishPending, pendingMessage]
  );

  const dismiss = useCallback(() => {
    if (savingRef.current) return;
    // X / backdrop: no comment saved, no email sent; keep draft and checkbox.
    finishPending({ clear: false });
  }, [finishPending]);

  const checkboxNode = (
    // describeChild keeps the tooltip as an aria-description; without it MUI
    // turns the title into the checkbox's aria-label, clobbering its name.
    <Tooltip title={NOTIFY_IMMEDIATELY_TOOLTIP} arrow describeChild>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={notifyChecked}
            onChange={(event) => setNotifyChecked(event.target.checked)}
          />
        }
        label={
          <Typography
            sx={{
              fontSize: "0.9375rem",
              color: notifyChecked ? "text.primary" : "text.secondary",
            }}
          >
            Notify immediately
          </Typography>
        }
        sx={{ ml: 0, mr: 0 }}
      />
    </Tooltip>
  );

  const dialogNode = (
    <ConfirmDialog
      open={pendingMessage !== null}
      header="Send notification email?"
      loading={saving}
      onConfirm={() => saveAndResolve(true)}
      onClose={dismiss}
      minWidth="560px"
      maxWidth="560px"
      actions={
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Button startIcon={<Send />} disabled={saving} onClick={() => saveAndResolve(true)}>
            Yes, Comment & Send Email
          </Button>
          <Button disabled={saving} onClick={() => saveAndResolve(false)}>
            Comment Without Notifying
          </Button>
        </Box>
      }
    >
      <DialogContentText>{CONFIRM_DIALOG_INFO}</DialogContentText>
      {/* Comment label + value; pre-wrap keeps the author's line breaks. */}
      <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 1 }}>
        <Grid size={2.5}>
          <DialogContentText
            sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}
          >
            Comment:
          </DialogContentText>
        </Grid>
        <Grid size={9.5}>
          <DialogContentText
            sx={{
              fontSize: "0.875rem",
              color: "text.primary",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {pendingMessage}
          </DialogContentText>
        </Grid>
      </Grid>
    </ConfirmDialog>
  );

  return { checkboxNode, dialogNode, handleSubmit };
}
